import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { memo, useCallback, useEffect, useMemo, useState, } from 'react';
import { Box, IconButton, Slider, Typography } from '@mui/material';
import { AddCircleOutline, RemoveCircleOutline } from '@mui/icons-material';
import { useCallbacks } from 'shared/hooks/utils';
import { AnalogOutput } from 'z3k-config';
import { useAppSelector } from 'shared/hooks/redux/hooks';
import { useDeviceConfig } from 'console/shared/contexts/device/config';
import { useCustomControl } from '../../provider';
export const CustomControlSlider = memo(function CustomControlSliderImpl(props) {
    const { online, state_value, onSendCommand } = props;
    const { device_id, z3k_config } = useDeviceConfig();
    const { id, id_active } = useCustomControl();
    const { max_value, min_value, step, mult, unit } = useMemo(() => {
        const analog_outputs = AnalogOutput.get.all(z3k_config);
        const analog_output = analog_outputs.find((output) => output.id === id_active);
        if (analog_output) {
            return analog_output;
        }
        return {
            max_value: 20,
            min_value: 0,
            step: 1,
            mult: 1,
            unit: AnalogOutput.Unit.Voltage,
        };
    }, [z3k_config, id_active]);
    const cached_state = useAppSelector((state) => state.z3k_analog_output_value?.by_device_id?.[device_id])?.[id]?.value ?? null;
    const [value, setValue] = useState(state_value);
    useEffect(() => {
        if (cached_state) {
            setValue(cached_state);
        }
        else {
            setValue(state_value);
        }
    }, [state_value]);
    const onChangeValue = useCallbacks([setValue, onSendCommand]);
    const clickMinus = useCallback(() => {
        if (value === null) {
            return onChangeValue(max_value);
        }
        if (value - step >= min_value) {
            return onChangeValue(value - step);
        }
        return onChangeValue(min_value);
    }, [onChangeValue, value, min_value, step, max_value]);
    const clickPlus = useCallback(() => {
        if (value === null) {
            return onChangeValue(min_value);
        }
        if (value + step <= max_value) {
            return onChangeValue(value + step);
        }
        return onChangeValue(max_value);
    }, [onChangeValue, value, min_value, step, max_value]);
    const onMoveSlider = useCallback((_, value) => {
        return !Array.isArray(value) ? setValue(value) : undefined;
    }, [setValue]);
    const onCommitChangesFromSlider = useCallback((_, value) => !Array.isArray(value) ? onSendCommand(value) : undefined, [onSendCommand]);
    return (_jsxs(Box, { display: "flex", flexDirection: "column", mt: 1, children: [_jsxs(Box, { display: "flex", justifyContent: "space-between", mx: 6, my: -1, children: [_jsx(Typography, { variant: "body2", color: "textSecondary", children: min_value / mult }), _jsx(Typography, { variant: "body2", color: "textSecondary", children: max_value / mult })] }), _jsxs(Box, { display: "flex", alignItems: "center", justifyContent: "space-between", children: [_jsx(IconButton, { size: "small", onClick: clickMinus, disabled: !online, children: _jsx(RemoveCircleOutline, {}) }), _jsx(Box, { flexGrow: 1, mx: 1, children: _jsx(Slider, { value: value ?? (min_value + max_value) / 2, min: min_value, max: max_value, step: step, valueLabelDisplay: "off", onChange: onMoveSlider, onChangeCommitted: onCommitChangesFromSlider, disabled: !online }) }), _jsx(IconButton, { size: "small", onClick: clickPlus, disabled: !online, children: _jsx(AddCircleOutline, {}) })] }), _jsx(Box, { mt: -1, children: _jsx(Typography, { variant: "subtitle1", textAlign: "center", children: value != null
                        ? `${value / mult} ${AnalogOutput.unit_select[unit].unit_abb}`
                        : 'Не установлено' }) })] }));
});
